import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo, type JobCategory } from '../model/metaInfo';
import type {
  StringMap,
  CreateMetaInfoRequest,
  CreateMetaInfoResponse,
  UpdateMetaInfoRequest,
  UpdateMetaInfoResponse,
  FetchMetaInfoRequest,
  FetchMetaInfoResponse,
  ListMetaInfoRequest,
  ListMetaInfoResponse,
} from '../model/types';

/**
 * Generic queryable MetaInfo pointer store (DDB-backed on the service side).
 *
 * Items, keys and updates are opaque `Record<string,string>` — this store knows
 * nothing about templates/tasks/estimates. Higher layers (DocumentStore<T>)
 * give the rows meaning.
 *
 * Calls {@link ApiClient} only. Pure TS — no React.
 */

const PATHS = {
  create: '/store/metainfo/create',
  update: '/store/metainfo/update',
  fetch: '/store/metainfo/fetch',
  list: '/store/metainfo/list',
} as const;

/** Query parameters accepted by {@link MetaInfoStore.listMetaInfo}. */
export interface ListMetaInfoQuery {
  indexName?: string;
  keyConditionExpression?: string;
  expressionAttributeValues?: StringMap;
  expressionAttributeNames?: StringMap;
  filterExpression?: string;
  limit?: number;
}

export class MetaInfoStore {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
    /** Job category stamped on this store's requests (default DOCUMENT_MANAGEMENT). */
    private readonly jobCategory: JobCategory = 'DOCUMENT_MANAGEMENT',
  ) {}

  async createMetaInfo(item: StringMap): Promise<CreateMetaInfoResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'CreateMetaInfo',
      jobCategory: this.jobCategory,
      requestType: 'CREATE_META_INFO',
    });
    return this.apiClient.post<CreateMetaInfoRequest, CreateMetaInfoResponse>(
      PATHS.create,
      { metaInfo, item },
    );
  }

  async updateMetaInfo(
    key: StringMap,
    updates: StringMap,
  ): Promise<UpdateMetaInfoResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'UpdateMetaInfo',
      jobCategory: this.jobCategory,
      requestType: 'UPDATE_META_INFO',
    });
    return this.apiClient.post<UpdateMetaInfoRequest, UpdateMetaInfoResponse>(
      PATHS.update,
      { metaInfo, key, updates },
    );
  }

  async fetchMetaInfo(key: StringMap): Promise<FetchMetaInfoResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'FetchMetaInfo',
      jobCategory: this.jobCategory,
      requestType: 'FETCH_META_INFO',
    });
    return this.apiClient.post<FetchMetaInfoRequest, FetchMetaInfoResponse>(
      PATHS.fetch,
      { metaInfo, key },
    );
  }

  async listMetaInfo(query: ListMetaInfoQuery = {}): Promise<ListMetaInfoResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'ListMetaInfo',
      jobCategory: this.jobCategory,
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<ListMetaInfoRequest, ListMetaInfoResponse>(
      PATHS.list,
      { metaInfo, ...query },
    );
  }
}
